import L from 'leaflet';
import PropTypes from 'prop-types';
import { useRef } from 'react';

import useLeafletLayer from './useLeafletLayer';

const TILE_PX = 256;
// Like getSecuredImg: HTTP/2 lifts the per-origin cap, the gateway's flood ceiling does not
const MAX_IN_FLIGHT = 6;

//
// The uploaded tiles of a plan, fetched with the session's bearer token (Leaflet's own
// TileLayer cannot send it). The map's CRS is planMapCrs() built from the plan's layout and
// rectangle, so map zoom z is the plan's tile level z.
//
const PlanGridLayer = L.GridLayer.extend({
  initialize(options) {
    L.GridLayer.prototype.initialize.call(this, options);
    this.waiting = [];
    this.unloaded = new WeakSet();
    this.inFlight = 0;
  },

  createTile(coords, done) {
    const tile = document.createElement('img');
    this.waiting.push({
      tile,
      coords,
      done,
    });
    this.pump();
    return tile;
  },

  pump() {
    const { fetchTile } = this.options;

    while ((this.inFlight < MAX_IN_FLIGHT) && this.waiting.length) {
      const { tile, coords, done } = this.waiting.shift();
      if (!this.unloaded.has(tile)) {
        this.inFlight += 1;
        fetchTile(coords.z, coords.x, coords.y).then(
          (blob) => {
            const url = URL.createObjectURL(blob);
            tile.onload = () => {
              URL.revokeObjectURL(url);
              done(null, tile);
            };
            tile.src = url;
          },
          (err) => done(err, tile),
        ).finally(() => {
          this.inFlight -= 1;
          this.pump();
        });
      }
    }
  },
});

function LeafletPlanTileLayer(props) {
  const {
    fetchTile,
    maxZoom,
    opacity = 1,
  } = props;
  const propsRef = useRef({
    fetchTile,
    maxZoom,
    opacity,
  });

  useLeafletLayer(
    () => {
      const layer = new PlanGridLayer({
        fetchTile: propsRef.current.fetchTile,
        tileSize: TILE_PX,
        minNativeZoom: 0,
        maxNativeZoom: propsRef.current.maxZoom,
        opacity: propsRef.current.opacity,
        updateWhenZooming: false,
        keepBuffer: 2,
      });
      layer.on('tileunload', ({ tile }) => layer.unloaded.add(tile));
      return layer;
    },
  );

  return null;
}

LeafletPlanTileLayer.propTypes = {
  // (z, x, y) => Promise<Blob>
  fetchTile: PropTypes.func.isRequired,
  maxZoom: PropTypes.number.isRequired,
  opacity: PropTypes.number,
};

export default LeafletPlanTileLayer;
